import { useState, useEffect, useCallback, useRef } from 'react';

import {
  Box,
  Typography,
  Button,
  TextField,
  IconButton,
  Chip,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  MedicalServices as MedicalIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { servicesService } from './services.service';
import { Service } from './services.dto';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';

const Services = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { token, isAuthenticated, loading: authLoading, logout } = useAuth();
  const { showSuccess, showError, showToast } = useToast();

  // Search term and page live in the URL (?search=...&page=...) instead of
  // plain component state - searching, then opening a service to edit,
  // then pressing Back used to remount this page with fresh useState
  // defaults (search cleared, page reset to 1) even though the browser
  // genuinely navigated "back" to what should have been the same search
  // results. Reading the initial value from the URL and keeping it synced
  // means the back button restores the exact URL (and therefore the exact
  // search/page) the user was looking at.
  const [searchParams, setSearchParams] = useSearchParams();
  const initialSearch = searchParams.get('search') || '';
  const initialPage = Math.max(
    1,
    parseInt(searchParams.get('page') || '1', 10) || 1
  );

  // State management
  const [searchTerm, setSearchTerm] = useState(initialSearch);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(initialSearch);
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [totalPages, setTotalPages] = useState(1);
  const [totalServices, setTotalServices] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [serviceToDelete, setServiceToDelete] = useState<number | null>(null);
  const itemsPerPage = 10;
  const isFirstRender = useRef(true);

  // Set up logout callback for session expired
  useEffect(() => {
    servicesService.setLogoutCallback(logout);
  }, [logout]);

  // Debounce search term
  useEffect(() => {
    console.log('🔍 Search term changed to:', searchTerm);
    const timer = setTimeout(() => {
      console.log(
        '⏱️ Debounce complete - setting debouncedSearchTerm to:',
        searchTerm
      );
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => {
      console.log('🧹 Clearing debounce timeout');
      clearTimeout(timer);
    };
  }, [searchTerm]);

  // Reset page to 1 when the search term actually changes - but not on
  // the very first render, where debouncedSearchTerm/currentPage were
  // just initialized together from the URL (?search=x&page=3) and
  // shouldn't stomp the restored page back to 1.
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    console.log('📄 Debounced search term changed, resetting page to 1');
    setCurrentPage(1);
  }, [debouncedSearchTerm]);

  // Keep the URL in sync with the current search/page so Back actually
  // returns to this exact view (see the comment above searchParams).
  useEffect(() => {
    const params: Record<string, string> = {};
    if (debouncedSearchTerm) params.search = debouncedSearchTerm;
    if (currentPage > 1) params.page = String(currentPage);
    setSearchParams(params, { replace: true });
    // Deliberately not depending on setSearchParams - react-router-dom
    // doesn't guarantee it's referentially stable across renders, and it
    // isn't meant to be treated as a value that changes this effect's
    // behavior.
  }, [debouncedSearchTerm, currentPage]);

  // Fetch services from API
  const fetchServices = useCallback(async () => {
    if (!token || !isAuthenticated) {
      setLoading(false);
      return;
    }

    console.log(
      '🔄 Fetching services - Page:',
      currentPage,
      'Search:',
      debouncedSearchTerm
    );

    try {
      setLoading(true);
      setError(null);

      const response = await servicesService.getServices(token, {
        page: currentPage,
        limit: itemsPerPage,
        search: debouncedSearchTerm || undefined,
      });

      setServices(response.data || []);
      setTotalPages(response.meta?.lastPage || 1);
      setTotalServices(response.meta?.total || 0);
    } catch (err) {
      console.error('Error fetching services:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch services');
      setServices([]);
    } finally {
      setLoading(false);
    }
  }, [token, isAuthenticated, currentPage, debouncedSearchTerm, itemsPerPage]);

  useEffect(() => {
    fetchServices();
  }, [fetchServices]);

  // Handlers
  const handleDeleteClick = (id: number) => {
    setServiceToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!token || !serviceToDelete) return;

    try {
      await servicesService.deleteService(token, serviceToDelete);
      showSuccess('Service deleted successfully');
      setDeleteDialogOpen(false);
      setServiceToDelete(null);

      // If current page becomes empty after delete, go to previous page
      if (services.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      } else {
        fetchServices();
      }
    } catch (err) {
      console.error('Error deleting service:', err);
      showError(
        err instanceof Error ? err.message : 'Failed to delete service'
      );
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setServiceToDelete(null);
  };

  const handleEdit = (id: number) => {
    navigate(`/services/edit/${id}`);
  };

  const handleAddService = () => {
    navigate('/services/add');
  };

  const handlePageChange = (
    _event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    console.log(
      '📄 Pagination clicked - Changing page from',
      currentPage,
      'to',
      page
    );
    setCurrentPage(page);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setDebouncedSearchTerm('');
  };

  // Check for success message from navigation state (e.g., after creating or editing a service)
  //
  // window.history.replaceState() below only clears the *browser's* raw
  // history state - React Router's own useLocation() keeps returning the
  // same location.state object across re-renders regardless (it isn't
  // re-derived from window.history.state on every render), so this effect
  // still saw a truthy state.message on the next render and fired again.
  // In dev, React 18 StrictMode deliberately double-invokes every effect
  // on mount on top of that - so the same toast queued 2-3 times for a
  // single navigation. shownMessageRef makes this idempotent: the same
  // state object only ever produces one toast, however many times the
  // effect actually runs.
  const shownMessageRef = useRef<unknown>(null);
  useEffect(() => {
    const state = location.state as {
      message?: string;
      severity?: 'success' | 'error';
    } | null;
    if (state?.message && shownMessageRef.current !== location.state) {
      shownMessageRef.current = location.state;
      showToast(state.message, state.severity || 'success');
      // Clear the location state to prevent showing the message again on re-render
      window.history.replaceState({}, document.title);
    }
  }, [location.state, showToast]);

  // Loading state
  if (authLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  // Debug pagination state
  console.log(
    '🔢 Pagination State - loading:',
    loading,
    'totalPages:',
    totalPages,
    'currentPage:',
    currentPage,
    'totalServices:',
    totalServices
  );

  // Render
  return (
    <Box>
      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
        flexWrap="wrap"
        gap={2}
      >
        <Typography variant="h4">Medical Services</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddService}
          disabled={!isAuthenticated}
        >
          Add Service
        </Button>
      </Box>

      {/* Search Bar */}
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search services..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          disabled={loading}
          InputProps={{
            startAdornment: (
              <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
            ),
            endAdornment: (
              <Box display="flex" alignItems="center" gap={1}>
                {searchTerm !== debouncedSearchTerm && (
                  <CircularProgress size={20} />
                )}
                {searchTerm && (
                  <IconButton
                    size="small"
                    onClick={handleClearSearch}
                    disabled={loading}
                    aria-label="Clear search"
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            ),
          }}
        />
      </Box>

      {/* Stats */}
      {/* {isAuthenticated && !loading && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary">
            Total Services: {totalServices}
          </Typography>
        </Box>
      )} */}

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Services Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>Image</TableCell>
                <TableCell>Parent Service Title</TableCell>
                <TableCell>Service Title</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Short Description</TableCell>
                <TableCell>Price</TableCell>
                <TableCell>Is Available</TableCell>
                <TableCell>Sort Order</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                    <Typography variant="body2" sx={{ mt: 2 }}>
                      Loading services...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : services.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      {searchTerm
                        ? 'No services found matching your search'
                        : 'No services found'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                services.map((service, index) => (
                  <TableRow key={service.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {(currentPage - 1) * 10 + index + 1}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Avatar
                        variant="rounded"
                        sx={{ width: 50, height: 50 }}
                        src={service.image_main || service.image}
                        alt={service.title}
                      >
                        <MedicalIcon />
                      </Avatar>
                    </TableCell>
                    <TableCell>
                      <Typography variant="subtitle2" fontWeight="bold">
                        {service.parent_id
                          ? service.parent_title
                          : 'Parent Service'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="subtitle2" fontWeight="bold">
                        {service.title}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          maxWidth: 300,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {service.description}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          maxWidth: 300,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {service.shortDescription}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {service.formattedPrice}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={
                          service.isAvailable === 1
                            ? 'Available'
                            : 'Unavailable'
                        }
                        color={
                          service.isAvailable === 1 ? 'success' : 'warning'
                        }
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {service.sortOrder}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={service.status === 1 ? 'Active' : 'Inactive'}
                        color={service.status === 1 ? 'success' : 'error'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Box display="flex" gap={0.5} justifyContent="center">
                        <IconButton
                          color="primary"
                          onClick={() => handleEdit(service.id)}
                          size="small"
                          disabled={loading}
                          title="Edit Service"
                        >
                          <EditIcon />
                        </IconButton>
                        {/* <IconButton
                          color={service.status === 1 ? 'warning' : 'success'}
                          onClick={() => handleToggleStatus(service.id)}
                          size="small"
                          disabled={loading}
                          title={service.status === 1 ? 'Deactivate' : 'Activate'}
                        >
                          {service.status === 1 ? '⏸️' : '▶️'}
                        </IconButton> */}
                        <IconButton
                          color="error"
                          onClick={() => handleDeleteClick(service.id)}
                          size="small"
                          disabled={loading}
                          title="Delete Service"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Pagination */}
      {!loading && totalPages > 1 ? (
        <Box display="flex" justifyContent="center" mt={3}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
            size="large"
            showFirstButton
            showLastButton
          />
        </Box>
      ) : (
        <Box display="flex" justifyContent="center" mt={3}>
          <Typography variant="caption" color="text.secondary">
            {loading
              ? 'Loading...'
              : `Showing page ${currentPage} of ${totalPages}`}
          </Typography>
        </Box>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this service? This action cannot be
            undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Services;
